// components/DataEntry/DataEntryForm.jsx — transaction-aware data entry
import React, { useState } from "react";
import {
  Text, Input, Button, Field, Textarea, Divider,
  MessageBar, MessageBarBody, Spinner, Badge,
} from "@fluentui/react-components";
import { CheckmarkCircle24Regular, Add24Regular, Info24Regular, LockClosed24Regular } from "@fluentui/react-icons";
import { useSchema } from "../../context/SchemaContext";
import { useNavigate } from "react-router-dom";
import { useUserAccess } from "../../context/UserAccessContext";

// Keys computed automatically from transaction collections — cannot be entered directly
const AUTO_COMPUTED = new Set([
  "contractsExecuted", "contractsAwarded",
  "suppliersOnboarded", "rfxIssued",
  "supplierPerformanceMonitoring", "stakeholderEngagement",
  "poCount", "poReceipted", "poAvgCycleTime", "poSpend",
  "amountMinorTB", "amountMajorTB", "cscMemos",
  "totalCostSavings", "costSavingsCount",
]);

const DIESEL_MAP = {
  dieselOpeningStock: "openingStock",
  dieselSupply:       "supply",
  dieselConsumption:  "consumption",
  dieselClosingStock: "closingStock",
};

const PROJECT_KEYS = ["uyoPortacabins", "uyoBaseProgress", "cateringProgress"];

function SectionHeader({ label, icon, count, note }) {
  return (
    <div style={{ display:"flex", alignItems:"center", gap:10, marginBottom:14, marginTop:22 }}>
      <div style={{ width:32, height:32, borderRadius:8, background:"linear-gradient(135deg,#0052CC,#0066FF)", display:"flex", alignItems:"center", justifyContent:"center", color:"#fff", fontSize:16, flexShrink:0 }}>
        {icon}
      </div>
      <Text weight="semibold" size={400} style={{ color:"#201F1E" }}>{label}</Text>
      {count != null && <Badge appearance="tint" color="informative" style={{ fontSize:10 }}>{count} fields</Badge>}
      {note && <Badge appearance="tint" color="warning" style={{ fontSize:10 }}>{note}</Badge>}
      <div style={{ flex:1, height:1, background:"#EDEBE9", marginLeft:8 }} />
    </div>
  );
}

function FieldInput({ entity, value, onChange }) {
  const hint = entity.unit ? `Unit: ${entity.unit}` : "";
  if (entity.type === "text") {
    return (
      <Field label={entity.name} hint={hint}>
        <Textarea value={value} onChange={(e) => onChange(e.target.value)} rows={3} placeholder={`Enter ${entity.name.toLowerCase()}...`} style={{ width:"100%", resize:"vertical" }} />
      </Field>
    );
  }
  if (entity.type === "percentage") {
    return (
      <Field label={`${entity.name} (0–1)`} hint="Enter decimal e.g. 0.75 for 75%">
        <Input type="number" min="0" max="1" step="0.01" value={value} onChange={(e) => onChange(e.target.value)} placeholder="0.00" style={{ width:"100%" }} />
      </Field>
    );
  }
  return (
    <Field label={entity.name} hint={hint}>
      <Input type="number" value={value} onChange={(e) => onChange(e.target.value)} placeholder="0" style={{ width:"100%" }} />
    </Field>
  );
}

export default function DataEntryForm({ onBulkAdd, loading }) {
  const { isAdmin } = useUserAccess();
  const { entitiesByCategory, activeEntities } = useSchema();
  const navigate = useNavigate();
  const [weekEnding, setWeekEnding] = useState("");
  const [fieldValues, setFieldValues] = useState({});
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState(null);

  if (!isAdmin) return (
    <div style={{ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", padding: "80px 24px", textAlign: "center" }}>
      <div style={{ width: 56, height: 56, borderRadius: 16, background: "#FDE7E9", display: "flex", alignItems: "center", justifyContent: "center", marginBottom: 16 }}>
        <LockClosed24Regular style={{ fontSize: 26, color: "#C4262E" }} />
      </div>
      <Text size={500} weight="semibold" style={{ color: "#001A3D", display: "block", marginBottom: 8 }}>Admin Access Required</Text>
      <Text style={{ color: "#7E8CA0", fontSize: 13 }}>Data entry is restricted to administrators only.</Text>
    </div>
  );

  const setField = (key) => (val) => setFieldValues((prev) => ({ ...prev, [key]: val }));

  const handleSubmit = async () => {
    if (!weekEnding) { setError("Week Ending date is required."); return; }
    setError(null);
    setSuccess(false);
    try {
      const txns = {};

      // Notes & Highlights → processNotes
      const piText   = fieldValues["processImprovement"] || "";
      const hlText   = fieldValues["highlights"] || "";
      if (piText.trim() || hlText.trim()) {
        txns.processNotes = [{ weekEnding, text: piText, highlights: hlText }];
      }

      // Diesel → dieselRecords
      const dieselEntry = {};
      let hasDiesel = false;
      Object.entries(DIESEL_MAP).forEach(([schemaKey, txKey]) => {
        const v = fieldValues[schemaKey];
        if (v != null && v !== "") { dieselEntry[txKey] = parseFloat(v); hasDiesel = true; }
      });
      if (hasDiesel) txns.dieselRecords = [{ weekEnding, ...dieselEntry }];

      // Logistics → logisticsRecords
      const trips = fieldValues["uyoLogisticsTrips"];
      const spend = fieldValues["uyoLogisticsSpend"];
      if ((trips != null && trips !== "") || (spend != null && spend !== "")) {
        txns.logisticsRecords = [{
          weekEnding,
          trips: trips != null && trips !== "" ? parseFloat(trips) : null,
          spend: spend != null && spend !== "" ? parseFloat(spend) : null,
        }];
      }

      // Project progress → projectUpdates
      const projUpdates = PROJECT_KEYS
        .filter(k => fieldValues[k] != null && fieldValues[k] !== "")
        .map(k => ({ weekEnding, key: k, progress: parseFloat(fieldValues[k]) }));
      if (projUpdates.length) txns.projectUpdates = projUpdates;

      if (!Object.keys(txns).length) {
        setError("No data entered. Fill in at least one field before saving.");
        return;
      }

      await onBulkAdd(txns);
      setSuccess(true);
      setWeekEnding("");
      setFieldValues({});
      setTimeout(() => setSuccess(false), 4000);
    } catch (err) {
      setError(err.message || "Failed to save record.");
    }
  };

  const handleClear = () => { setWeekEnding(""); setFieldValues({}); setError(null); };

  // Split entities into directly enterable vs auto-computed per category
  const categoriesWithEntities = Object.entries(entitiesByCategory);

  return (
    <div style={{ background:"#fff", borderRadius:16, padding:32, boxShadow:"0 2px 12px rgba(0,0,0,0.06)", border:"1px solid #EDEBE9", maxWidth:960 }}>
      <div style={{ display:"flex", alignItems:"flex-start", justifyContent:"space-between", marginBottom:6 }}>
        <div>
          <Text weight="bold" size={500} style={{ color:"#201F1E", display:"block" }}>New Weekly SCM Record</Text>
          <Text style={{ color:"#605E5C", fontSize:13 }}>
            {activeEntities.filter(e => !AUTO_COMPUTED.has(e.key)).length} directly enterable fields · transaction counts are auto-computed
          </Text>
        </div>
        <Button appearance="subtle" icon={<Add24Regular />} onClick={() => navigate("/entities")} style={{ fontSize:12 }}>
          Manage Entities
        </Button>
      </div>

      {success && (
        <MessageBar intent="success" style={{ marginTop:14, borderRadius:8 }}>
          <MessageBarBody><CheckmarkCircle24Regular style={{ verticalAlign:"middle", marginRight:6 }} />Record saved successfully!</MessageBarBody>
        </MessageBar>
      )}
      {error && (
        <MessageBar intent="error" style={{ marginTop:14, borderRadius:8 }}>
          <MessageBarBody>{error}</MessageBarBody>
        </MessageBar>
      )}

      {/* Info banner */}
      <div style={{ marginTop:16, padding:"10px 14px", background:"#EBF3FF", borderRadius:10, border:"1px solid #C7E0FF", display:"flex", gap:10, alignItems:"flex-start" }}>
        <Info24Regular style={{ color:"#0052CC", flexShrink:0, marginTop:1 }} />
        <Text style={{ fontSize:12, color:"#003A8C", lineHeight:1.6 }}>
          <strong>Contracts, POs, CSC Memos, Tender Board, RFX and Supplier Performance</strong> counts are automatically computed from your uploaded transaction records. Use <strong>Excel Upload</strong> or the <strong>Reports</strong> page to add those transactions.
        </Text>
      </div>

      {/* Week picker */}
      <div style={{ display:"flex", alignItems:"flex-end", gap:16, marginTop:20 }}>
        <Field label="Week Ending *" required style={{ width:220 }}>
          <Input type="date" value={weekEnding} onChange={(e) => setWeekEnding(e.target.value)} style={{ width:"100%" }} />
        </Field>
        <Text style={{ fontSize:12, color:"#8A8886", marginBottom:6 }}>Required · All other fields are optional</Text>
      </div>

      {/* Dynamic sections */}
      {categoriesWithEntities.map(([category, entities]) => {
        const enterable  = entities.filter(e => !AUTO_COMPUTED.has(e.key));
        const computed   = entities.filter(e => AUTO_COMPUTED.has(e.key));

        return (
          <div key={category}>
            {enterable.length > 0 && (
              <>
                <SectionHeader label={category} icon={entities[0]?.icon || "📋"} count={enterable.length} />
                <div style={{ display:"grid", gridTemplateColumns: enterable.some(e => e.type === "text") ? "repeat(auto-fill, minmax(320px,1fr))" : "repeat(auto-fill, minmax(200px,1fr))", gap:14 }}>
                  {enterable.map(entity => (
                    <FieldInput
                      key={entity.id}
                      entity={entity}
                      value={fieldValues[entity.key] || ""}
                      onChange={setField(entity.key)}
                    />
                  ))}
                </div>
              </>
            )}

            {computed.length > 0 && (
              <div style={{ marginTop: enterable.length ? 12 : 22, padding:"10px 14px", background:"#F9FAFB", borderRadius:10, border:"1px dashed #D1D5DB" }}>
                <Text style={{ fontSize:11, fontWeight:700, color:"#9CA3AF", textTransform:"uppercase", letterSpacing:0.6, display:"block", marginBottom:6 }}>
                  {enterable.length === 0 ? `${category} — ` : ""}Auto-computed from transactions
                </Text>
                <div style={{ display:"flex", flexWrap:"wrap", gap:6 }}>
                  {computed.map(e => (
                    <Badge key={e.id} appearance="outline" style={{ fontSize:11, color:"#6B7280" }}>{e.icon} {e.name}</Badge>
                  ))}
                </div>
              </div>
            )}
          </div>
        );
      })}

      <Divider style={{ margin:"24px 0" }} />

      <div style={{ display:"flex", gap:12, justifyContent:"flex-end" }}>
        <Button appearance="outline" onClick={handleClear} disabled={loading}>Clear Form</Button>
        <Button
          appearance="primary"
          onClick={handleSubmit}
          disabled={loading}
          icon={loading ? <Spinner size="tiny" /> : null}
          style={{ background:"#0052CC", minWidth:140 }}
        >
          {loading ? "Saving..." : "Save Record"}
        </Button>
      </div>
    </div>
  );
}
